import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';
import * as turf from '@turf/turf';
import {
  COORD_LAT,
  COORD_LNG,
  COORD_LIMITS,
} from '../../servicios/coordinates.helper';
import { IGeoJSONCircle, IUbicacion, IUbicacionBase } from 'modelos/src';

/**
 * CentrosAtencionLayerManager
 *
 * Manager para renderizar centros de atención (bases de operaciones) en el mapa.
 *
 * Características:
 * - Icono de centro de atención (centro-atencion.svg)
 * - Círculo de cobertura alrededor del centro (si tiene circuloArea)
 * - Sin clustering
 * - Icono: centro-atencion.svg (35x35px)
 *
 * Renderiza:
 * 1. Círculo de cobertura (área de influencia del centro)
 * 2. Marker del centro de atención
 */
@Injectable({
  providedIn: 'root',
})
export class CentrosAtencionLayerManager extends BaseEntityLayerManager<
  IUbicacionBase<'Centro de Atención'>
> {
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'centrosAtencion';
  readonly supportsClustering = false; // Los centros no se agrupan
  readonly clusterColor = '#0000FF'; // No usado, pero requerido por la base

  // IDs de layers
  private mainLayerId = 'centros-atencion-layer';
  private circleLayerId = 'centros-atencion-circles-layer';
  private circleSourceId = 'centros-atencion-circles-source';

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte coordenadas Web Mercator (EPSG:3857) a WGS84 (EPSG:4326)
   */
  private webMercatorToWgs84(x: number, y: number): [number, number] {
    const EARTH_RADIUS = 6378137; // Radio ecuatorial de la Tierra en metros
    const lng = (x / EARTH_RADIUS) * (180 / Math.PI);
    const lat =
      (2 * Math.atan(Math.exp(y / EARTH_RADIUS)) - Math.PI / 2) *
      (180 / Math.PI);
    return [lng, lat];
  }

  /**
   * Convierte un centro de atención a un Feature de GeoJSON (para el marker)
   */
  override entityToFeature(
    centro: IUbicacionBase<'Centro de Atención'>,
  ): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(centro);

    // Los centros tienen circuloArea.center con las coordenadas
    let coordinates: [number, number];

    if (
      centro.geojson?.coordinates &&
      Array.isArray(centro.geojson.coordinates)
    ) {
      const rawCoords: [number, number] = centro.geojson.coordinates as [
        number,
        number,
      ];

      // Detectar si las coordenadas están en Web Mercator (valores muy grandes)
      if (Math.abs(rawCoords[0]) > 180 || Math.abs(rawCoords[1]) > 90) {
        console.warn(
          `⚠️ Centro "${centro.identificacion}" en Web Mercator, convirtiendo a WGS84...`,
        );
        coordinates = this.webMercatorToWgs84(rawCoords[0], rawCoords[1]);
      } else {
        coordinates = rawCoords;
      }
    } else if (centro.geojson?.coordinates) {
      coordinates = centro.geojson.coordinates as [number, number];
    } else {
      // Fallback: Buenos Aires
      console.warn(
        `⚠️ Centro de atención ${centro._id} sin coordenadas válidas`,
      );
      coordinates = [-58.443832, -34.587386];
    }

    // Validar coordenadas finales (seguridad)
    if (
      coordinates[COORD_LNG] < COORD_LIMITS.LNG_MIN ||
      coordinates[COORD_LNG] > COORD_LIMITS.LNG_MAX ||
      coordinates[COORD_LAT] < COORD_LIMITS.LAT_MIN ||
      coordinates[COORD_LAT] > COORD_LIMITS.LAT_MAX
    ) {
      console.error(
        `❌ Centro "${centro.identificacion}" coordenadas inválidas después de conversión, usando fallback`,
      );
      coordinates = [-58.443832, -34.587386];
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id: centro._id,
        identificacion: centro.identificacion,
        tipoEmergencia: centro.valores?.tipoEmergencia,
        telefono: centro.valores?.telefono,
        email: centro.valores?.email,
        activo: centro.valores?.activo,
        // Datos completos para popup
      },
    };
  }

  /**
   * Crea un Feature de tipo Polygon para el círculo de cobertura
   */
  private createCircleFeature(
    centro: IUbicacionBase<'Centro de Atención'>,
  ): GeoJSON.Feature<GeoJSON.Polygon> | null {
    if (
      !(centro.geojson as IGeoJSONCircle)?.coordinates ||
      !(centro.geojson as IGeoJSONCircle)?.radius
    ) {
      return null;
    }

    let center = centro.geojson.coordinates as [number, number];
    const radius = (centro.geojson as IGeoJSONCircle).radius; // Asumiendo que está en metros

    // Detectar y convertir coordenadas Web Mercator si es necesario
    if (Math.abs(center[0]) > 180 || Math.abs(center[1]) > 90) {
      center = this.webMercatorToWgs84(center[0], center[1]);
    }

    // Usar Turf.js para crear un círculo
    const circlePolygon = turf.circle([center[0], center[1]], radius, {
      steps: 64,
      units: 'meters',
    });

    return {
      ...circlePolygon,
      properties: {
        id: centro._id,
        identificacion: centro.identificacion,
        tipoEmergencia: centro.valores?.tipoEmergencia,
        radius: radius,
      },
    };
  }

  /**
   * Icono de centro de atención
   */
  override getIconExpression(): string {
    return 'centro-atencion-icon';
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea los layers para centros de atención
   */
  protected override createLayers(): void {
    if (!this.map) return;

    // 1. SOURCE para círculos
    if (!this.map.getSource(this.circleSourceId)) {
      this.map.addSource(this.circleSourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });
    }

    // 2. LAYER de círculos (renderizado primero, debajo de los markers)
    this.addLayer({
      id: this.circleLayerId,
      type: 'fill',
      source: this.circleSourceId,
      layout: {
        visibility: 'visible',
      },
      paint: {
        'fill-color': 'rgba(0, 0, 255, 0.1)', // Azul transparente
        'fill-outline-color': '#0000FF', // Azul sólido
      },
    });

    // 3. LAYER de borde de círculos
    this.addLayer({
      id: `${this.circleLayerId}-stroke`,
      type: 'line',
      source: this.circleSourceId,
      layout: {
        visibility: 'visible',
      },
      paint: {
        'line-color': '#0000FF', // Azul
        'line-width': 3,
      },
    });

    // 4. LAYER principal de centros de atención (markers)
    this.addLayer({
      id: this.mainLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        visibility: 'visible',
        'icon-image': this.getIconExpression(),
        'icon-size': 0.583, // SVG 60px → 35px (35/60 = 0.583)
        'icon-allow-overlap': true,
        'icon-padding': 0,
        'icon-rotation-alignment': 'viewport',
        'icon-pitch-alignment': 'viewport',
        'icon-anchor': 'bottom', // Anclar en la parte inferior como pins
      },
    });

    // IMPORTANTE: Registrar layers de círculos en layerIds para cleanup y para excluirlos de clicks
    // Los círculos son solo visuales (área de cobertura), no deben mostrar popups
    this.layerIds.push(this.circleLayerId);
    this.layerIds.push(`${this.circleLayerId}-stroke`);
  }

  // ========== ACTUALIZACIÓN DE DATOS ==========

  /**
   * Override de updateData para manejar tanto markers como círculos
   */
  override updateData(
    entities: IUbicacionBase<'Centro de Atención'>[],
  ): import('../core/interfaces/entity-layer-manager.interface').UpdateDataResult {
    if (!this.map) {
      console.warn(
        '⚠️ Mapa no inicializado, no se pueden actualizar centros de atención',
      );
      return {
        success: false,
        layersUpdated: [],
        sourcesUpdated: [],
        error: 'Mapa no inicializado',
      };
    }

    // 1. Actualizar markers (llamada al método padre)
    const result = super.updateData(entities);

    // 2. Actualizar círculos
    const circleFeatures: GeoJSON.Feature<GeoJSON.Polygon>[] = [];

    for (const centro of entities) {
      const circleFeature = this.createCircleFeature(centro);
      if (circleFeature) {
        circleFeatures.push(circleFeature);
      }
    }

    // Actualizar source de círculos
    const circleSource = this.map.getSource(
      this.circleSourceId,
    ) as mapboxgl.GeoJSONSource;
    if (circleSource) {
      circleSource.setData({
        type: 'FeatureCollection',
        features: circleFeatures,
      });
      result.sourcesUpdated.push(this.circleSourceId);
    }

    return result;
  }

  // ========== VISIBILIDAD GENERAL ==========

  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    // Actualizar visibilidad del marker layer
    if (this.map.getLayer(this.mainLayerId)) {
      this.map.setLayoutProperty(
        this.mainLayerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }

    // Actualizar visibilidad del circle layer
    if (this.map.getLayer(this.circleLayerId)) {
      this.map.setLayoutProperty(
        this.circleLayerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }

    // Actualizar visibilidad del stroke layer
    if (this.map.getLayer(`${this.circleLayerId}-stroke`)) {
      this.map.setLayoutProperty(
        `${this.circleLayerId}-stroke`,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  // ========== LIMPIEZA ==========

  override cleanup(): void {
    // Llamar al cleanup padre primero para remover todos los layers (incluyendo
    // circleLayerId y stroke) antes de intentar remover la source
    super.cleanup();

    // Recién ahora es seguro remover la source de círculos
    if (this.map && this.map.getSource(this.circleSourceId)) {
      this.map.removeSource(this.circleSourceId);
    }
  }
}
